<template>
  <section class="enterprise">
    <div class="enterprise__bg"></div>
    <div class="enterprise__overlay"></div>

    <div class="container enterprise__content">
      <div class="enterprise__text">
        <p class="enterprise__en">ENTERPRISE STRENGTH</p>
        <h2 class="enterprise__title">
          企业概览
          <span class="enterprise__dots">
            <i></i><i></i>
          </span>
        </h2>

        <div
            v-for="(group, gIndex) in dataGroups"
            :key="gIndex"
            class="enterprise__group"
        >
          <h3 class="enterprise__group-title">{{ group.title }}</h3>
          <div class="enterprise__stats">
            <div
                v-for="(item, iIndex) in group.items"
                :key="iIndex"
                class="enterprise__stat"
            >
              <div class="enterprise__stat-value">
                <template v-if="item.number !== undefined">
                  <span class="enterprise__number">{{ displayNumbers[item.key] || 0 }}</span>
                  <span class="enterprise__suffix">{{ item.suffix || '' }}</span>
                </template>
                <template v-else>
                  <span class="enterprise__text-value">{{ item.value }}</span>
                </template>
              </div>
              <div class="enterprise__stat-label">{{ item.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, onUnmounted, reactive } from 'vue'

/* ===================== 数据配置 ===================== */
const dataGroups = [
  {
    title: '服务规模',
    items: [
      { key: 'miniApp', number: 20000, suffix: '+', label: '小程序/App用户' },
      { key: 'escorts', number: 7000, suffix: '+', label: '注册陪诊师' },
      { key: 'promoters', number: 3000, suffix: '+', label: '推广大使' }
    ]
  },
  {
    title: '服务能力',
    items: [
      { value: '三大服务', label: '陪诊·陪护·绿通' },
      { value: '诊前诊中诊后', label: '全流程闭环服务' },
      { value: '24小时', label: '快速响应' }
    ]
  },
  {
    title: '平台与资质',
    items: [
      { key: 'dailyVisit', number: 200, suffix: '+', label: '日均访问人数' },
      { key: 'visitCount', number: 1000, suffix: '+', label: '访问次数' },
      { key: 'copyright', number: 4, suffix: '', label: '软件著作权' }
    ]
  }
]

/* ===================== 数字增长动画 ===================== */
const displayNumbers = reactive({})
let observer = null

const animateNumber = (key, target) => {
  const duration = 1600
  const start = performance.now()
  const step = (now) => {
    const progress = Math.min((now - start) / duration, 1)
    const easeOut = 1 - Math.pow(1 - progress, 3)
    displayNumbers[key] = Math.floor(easeOut * target)
    if (progress < 1) requestAnimationFrame(step)
    else displayNumbers[key] = target
  }
  requestAnimationFrame(step)
}

onMounted(() => {
  dataGroups.forEach(group => {
    group.items.forEach(item => {
      if (item.number !== undefined) displayNumbers[item.key] = 0
    })
  })

  observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            dataGroups.forEach(group => {
              group.items.forEach(item => {
                if (item.number !== undefined) {
                  animateNumber(item.key, item.number)
                }
              })
            })
            observer.disconnect()
          }
        })
      },
      { threshold: 0.2 }
  )
  const el = document.querySelector('.enterprise__stats')
  if (el) observer.observe(el)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
/* ===================== 外层：上移，与上下模块拉开间距 ===================== */
.enterprise {
  position: relative;
  padding: 80px 0 140px;         /* 上 80px，下 140px，整体上移 */
  overflow: hidden;
  min-height: 640px;
}

/* ===================== 背景图 ===================== */
.enterprise__bg {
  position: absolute;
  inset: 0;
  background-image: url('https://ltfxpz.oss-cn-shanghai.aliyuncs.com/other/%E5%8C%BB%E9%99%A2%E9%99%AA%E8%AF%8A%E5%9C%BA%E6%99%AF%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90%E9%9C%80%E6%B1%82.png');
  background-size: cover;
  background-position: right center;
  background-repeat: no-repeat;
  z-index: 0;
}

/* 遮罩：左侧白色渐变，右侧露出图片 */
.enterprise__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(255, 255, 255, 0.95) 40%,
      rgba(255, 255, 255, 0.6) 65%,
      rgba(255, 255, 255, 0.2) 100%
  );
  z-index: 1;
}

/* ===================== 内容区 ===================== */
.container {
  max-width: 1400px;
  margin: 0 100px;
  padding: 0 24px;
  position: relative;
  z-index: 2;
}
.enterprise__content {
  position: relative;
  z-index: 2;
}
.enterprise__text {
  max-width: 620px;
}

/* ===================== 标题 ===================== */
.enterprise__en {
  font-size: 14px;
  letter-spacing: 4px;
  color: #8a9aa8;
  margin-bottom: 16px;
}
.enterprise__title {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 42px;
  font-weight: 800;
  color: var(--gt-primary);
  line-height: 1.2;
  margin-bottom: 48px;
}
.enterprise__dots {
  display: inline-flex;
  gap: 6px;
  margin-left: 4px;
}
.enterprise__dots i {
  display: block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--gt-secondary);
  opacity: 0.5;
}
.enterprise__dots i:nth-child(2) {
  opacity: 1;
}

/* ===================== 数据组 ===================== */
.enterprise__group {
  margin-bottom: 48px;
}
.enterprise__group:last-child {
  margin-bottom: 0;
}
.enterprise__group-title {
  font-size: 19px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 24px;
  padding-left: 14px;
  border-left: 4px solid var(--gt-primary);
  line-height: 1.4;
}

/* 数据条目 */
.enterprise__stats {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
}
.enterprise__stat {
  display: flex;
  flex-direction: column;
  padding-right: 40px;
  margin-right: 80px;
  border-right: 1px solid #e8eef5;
  min-width: 100px;
}
.enterprise__stat:last-child {
  border-right: none;
  margin-right: 0;
  padding-right: 0;
}

.enterprise__stat-value {
  display: flex;
  align-items: baseline;
  color: var(--gt-primary);
  margin-bottom: 8px;
}
.enterprise__number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1px;
}
.enterprise__suffix {
  font-size: 24px;
  font-weight: 700;
  margin-left: 2px;
}
.enterprise__text-value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1.2;
}
.enterprise__stat-label {
  font-size: 14px;
  color: #8a9aa8;
  line-height: 1.5;
}

/* ===================== 响应式 ===================== */
@media (max-width: 1100px) {
  .enterprise {
    padding: 60px 0 100px;
    min-height: auto;
  }
  .enterprise__bg {
    background-position: center;
  }
  .enterprise__overlay {
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.98) 0%,
        rgba(255, 255, 255, 0.92) 60%,
        rgba(255, 255, 255, 0.7) 100%
    );
  }
  .enterprise__text {
    max-width: 100%;
  }
  .enterprise__title { font-size: 36px; margin-bottom: 40px; }
  .enterprise__group { margin-bottom: 40px; }
  .enterprise__group-title { font-size: 17px; margin-bottom: 20px; }
  .enterprise__stat {
    padding-right: 28px;
    margin-right: 28px;
  }
  .enterprise__number { font-size: 34px; }
  .enterprise__text-value { font-size: 22px; }
}

@media (max-width: 600px) {
  .enterprise { padding: 50px 0 80px; }
  .enterprise__title { font-size: 28px; margin-bottom: 32px; }
  .enterprise__group { margin-bottom: 32px; }
  .enterprise__group-title { font-size: 16px; margin-bottom: 16px; }
  .enterprise__stat {
    padding-right: 20px;
    margin-right: 20px;
    margin-bottom: 20px;
    min-width: 80px;
  }
  .enterprise__number { font-size: 26px; }
  .enterprise__suffix { font-size: 18px; }
  .enterprise__text-value { font-size: 18px; }
  .enterprise__stat-label { font-size: 12px; }
}
</style>
